import React, { useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { SearchFilterBarSelect } from './search-filter-bar-select';
import { Grid, useTheme } from '@mui/material';

export interface FilterField {
  key: string;
  label: string;
  options?: string[];
  allLabel?: string;
}

interface SearchFilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  filters: Record<string, string>;
  onFiltersChange: (filters: Record<string, string>) => void;
  filterFields?: FilterField[];
  shouldShowFilters?: boolean;
  placeholder?: string;
  label?: string;
}

export const SearchFilterBar = ({
  searchValue,
  onSearchChange,
  filters,
  onFiltersChange,
  filterFields = [],
  shouldShowFilters = true,
  placeholder = 'Search...',
  label = 'Search',
}: SearchFilterBarProps) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const updateFilter = (key: string, value: string) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    const cleared = Object.keys(filters).reduce((acc, key) => ({ ...acc, [key]: '' }), {});
    onFiltersChange(cleared);
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);
  const theme = useTheme();

  return (
    <Grid
      container
      sx={{
        width: '100%',
        minWidth: 200,
      }}
    >
      <Grid
        sx={{
          display: 'flex',
          flexGrow: 1,
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          label={label}
          placeholder={placeholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            flexGrow: 1,
            minWidth: 200,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="h-4 w-4 text-muted-foreground" />
              </InputAdornment>
            ),
            endAdornment: searchValue ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')}>
                  <CloseIcon className="h-4 w-4" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {shouldShowFilters && filterFields.length > 0 && (
          <IconButton
            onClick={() => setShowAdvanced(!showAdvanced)}
            sx={{
              border: '1px solid',
              borderColor: showAdvanced ? theme.palette.primary.light : theme.palette.grey[400],
              backgroundColor: showAdvanced ? 'inherit' : 'transparent',
              borderRadius: '0.5rem',
              flexShrink: 0,
            }}
          >
            <TuneIcon className="h-4 w-4" />
          </IconButton>
        )}
      </Grid>

      {showAdvanced && filterFields.length > 0 && (
        <Grid
          container
          sx={{
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: 'wrap',
          }}
        >
          <Grid
            container
            sx={{
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1,
              width: 'auto',
            }}
          >
            {filterFields.map(({ key, label, options = [], allLabel }) => (
              <SearchFilterBarSelect
                key={key}
                label={label}
                value={filters[key] || ''}
                options={options}
                allLabel={allLabel || `All ${label}s`}
                onChange={(value) => updateFilter(key, value)}
              />
            ))}
          </Grid>

          {hasActiveFilters && (
            <Button
              variant="text"
              size="small"
              onClick={clearFilters}
              startIcon={<CloseIcon className="h-3 w-3" />}
              sx={{ textTransform: 'none', borderRadius: '4px', mt: 1, mx: 1 }}
            >
              Clear
            </Button>
          )}
        </Grid>
      )}
    </Grid>
  );
};
